/* Park Friends: the "Add one more" rail in the cart drawer.
   ---------------------------------------------------------------------------------------
   The rail itself is CSS. One slide fills the view, scroll-snap holds it there, and a finger
   already scrolls it on a phone with no JavaScript at all. This file adds the three things a
   scroll container cannot do on its own:

     1. arrows, for a mouse
     2. the "2 / 3" counter, so the shopper knows the rail has more in it
     3. the price and the picture following the size that was picked

   That last one is not decoration. The theme's own onChangeProductItemVariant reaches for
   select.closest('.product-item').querySelector('.product-item-price'), which does not exist in
   this markup, so it throws and the price silently keeps showing the first variant's. The
   Hometown Dallas jersey runs $59.99 at Toddler 1T and $99.99 at the top of its size range, so
   that is a wrong price on screen, not a cosmetic miss. Ours reads the money string Liquid
   already rendered onto each option, so there is no client-side currency formatting to get
   wrong.

   Everything is delegated from the document, because assets/cart.js replaces .offcanvas-footer
   wholesale on every cart change and a listener bound to the rail would not survive it. */
(function () {
  'use strict';

  var RAIL = '[data-pf-carousel-rail]';

  function slides(rail) { return rail.querySelectorAll('.pf-upsell__slide'); }

  /* One slide plus the gap between two of them, and how many of those the track holds.
     Both read from the DOM rather than assumed, because the slide is a share of the rail and
     the rail is 390px wide on a phone and 635px in the desktop drawer.

     David, 2026-09-30: at desktop the rail shows two whole cards, so a pager that counted
     single slides would say "1 / 6" while six cards go past two at a time. It pages by what
     is actually in view. On a phone the track holds one, perView is 1, and the counter reads
     exactly what it read before. */
  function metrics(rail) {
    var first = rail.querySelector('.pf-upsell__slide');
    if (!first) return { step: rail.clientWidth, per: 1, page: rail.clientWidth };
    var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '0') || 0;
    var step = first.getBoundingClientRect().width + gap;
    if (!step) return { step: rail.clientWidth, per: 1, page: rail.clientWidth };
    /* the +1 absorbs the sub-pixel width a percentage slide lands on */
    var per = Math.max(1, Math.floor((rail.clientWidth + gap + 1) / step));
    return { step: step, per: per, page: step * per };
  }

  function paint(rail) {
    var root = rail.closest('[data-pf-carousel]');
    if (!root) return;
    var total = slides(rail).length;
    if (!total) return;
    var m = metrics(rail);
    var pages = Math.max(1, Math.ceil(total / m.per));
    var i = Math.min(Math.max(Math.round(rail.scrollLeft / m.page), 0), pages - 1);

    var count = root.querySelector('[data-pf-carousel-count]');
    if (count) count.textContent = pages > 1 ? (i + 1) + ' / ' + pages : '';

    /* A rail with nothing off screen has nowhere to go, so the arrows come off rather than
       sitting there greyed out next to something that cannot move. */
    var nav = root.querySelector('.pf-upsell__nav');
    if (nav) nav.hidden = total <= m.per;

    /* Measured against the real scroll position, not the page index: the last page is a
       short one whenever the slide count is not a multiple of what the track holds, and an
       index test would leave NEXT live at the end of the rail. */
    var end = rail.scrollWidth - rail.clientWidth;
    var prev = root.querySelector('[data-pf-carousel-prev]');
    var next = root.querySelector('[data-pf-carousel-next]');
    if (prev) prev.disabled = rail.scrollLeft <= 1;
    if (next) next.disabled = rail.scrollLeft >= end - 1;
  }

  function paintAll() {
    document.querySelectorAll(RAIL).forEach(paint);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-pf-carousel-prev],[data-pf-carousel-next]');
    if (!btn) return;
    event.preventDefault();
    var root = btn.closest('[data-pf-carousel]');
    var rail = root && root.querySelector(RAIL);
    if (!rail) return;
    var dir = btn.hasAttribute('data-pf-carousel-next') ? 1 : -1;
    rail.scrollBy({ left: dir * metrics(rail).page, behavior: 'smooth' });
  });

  /* scroll does not bubble, so the listener is registered in the capture phase. That is what
     lets one document-level handler serve a rail that gets replaced on every cart update. */
  document.addEventListener('scroll', function (event) {
    var el = event.target;
    if (el && el.nodeType === 1 && el.matches && el.matches(RAIL)) paint(el);
  }, true);

  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('[data-pf-upsell-variant]');
    if (!select) return;
    var slide = select.closest('.pf-upsell__slide');
    if (!slide) return;
    var option = select.options[select.selectedIndex];
    if (!option) return;

    var price = slide.querySelector('[data-pf-upsell-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    /* The struck compare-at follows the size too, and disappears on a size Shopify has no
       compare_at for. Liquid wrote both strings onto the option, so nothing is formatted here. */
    var compare = slide.querySelector('[data-pf-upsell-compare]');
    if (compare) {
      if (option.dataset.compare) {
        compare.textContent = option.dataset.compare;
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    var img = slide.querySelector('[data-pf-upsell-img]');
    if (img && option.dataset.variantImage) img.src = option.dataset.variantImage;
  });

  /* cart.js swaps .offcanvas-body and .offcanvas-footer out on every add, remove, quantity
     change and property edit, so the rail on screen is rarely the one that was there a moment
     ago and the counter has to be repainted for the new one. Scoped to the drawer wrapper,
     which does survive, and coalesced to one repaint per frame. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; paintAll(); });
  }

  function watch() {
    var host = document.querySelector('#offcanvas-cart-wrapper') || document.body;
    if (!host) return;
    new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watch(); paintAll(); });
  } else {
    watch();
    paintAll();
  }
})();
